import { useEffect, useRef } from 'react';

function GeodesicCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const angleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const generateNodes = (cx: number, cy: number, baseR: number) => {
      const nodes: [number, number, number][] = []; // x, y, ring
      nodes.push([cx, cy, 0]);
      for (let i = 0; i < 6; i++) {
        const a = (i * 60 - 90) * (Math.PI / 180);
        nodes.push([cx + baseR * 0.22 * Math.cos(a), cy + baseR * 0.22 * Math.sin(a), 1]);
      }
      for (let i = 0; i < 12; i++) {
        const a = (i * 30 - 90) * (Math.PI / 180);
        nodes.push([cx + baseR * 0.44 * Math.cos(a), cy + baseR * 0.44 * Math.sin(a), 2]);
      }
      for (let i = 0; i < 18; i++) {
        const a = (i * 20 - 90) * (Math.PI / 180);
        nodes.push([cx + baseR * 0.66 * Math.cos(a), cy + baseR * 0.66 * Math.sin(a), 3]);
      }
      for (let i = 0; i < 24; i++) {
        const a = (i * 15 - 90) * (Math.PI / 180);
        nodes.push([cx + baseR * 0.88 * Math.cos(a), cy + baseR * 0.88 * Math.sin(a), 4]);
      }
      for (let i = 0; i < 6; i++) {
        const a = (i * 60 - 60) * (Math.PI / 180);
        nodes.push([cx + baseR * Math.cos(a), cy + baseR * Math.sin(a), 5]);
      }
      return nodes;
    };

    const drawEdge = (x1: number, y1: number, x2: number, y2: number, alpha: number, color: string) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = color;
      ctx.globalAlpha = alpha;
      ctx.lineWidth = 0.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
    };

    const drawNode = (x: number, y: number, ring: number) => {
      const radius = ring === 0 ? 4 : ring <= 2 ? 2.5 : 1.5;
      const color = ring % 2 === 0 ? '#C97B63' : '#8B9D83';
      const alpha = ring === 0 ? 1 : 0.9 - ring * 0.1;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha;
      ctx.fill();
      if (ring <= 2) {
        ctx.beginPath();
        ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
        ctx.strokeStyle = color;
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = alpha * 0.3;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };

    const draw = () => {
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      angleRef.current += 0.002;
      const angle = angleRef.current;

      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const baseR = Math.min(canvas.width, canvas.height) * 0.42;

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      ctx.translate(-cx, -cy);

      const nodes = generateNodes(cx, cy, baseR);

      // Draw outer circle
      ctx.beginPath();
      ctx.arc(cx, cy, baseR * 1.02, 0, Math.PI * 2);
      ctx.strokeStyle = '#8B9D83';
      ctx.globalAlpha = 0.25;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.globalAlpha = 1;

      // Draw radial lines from center
      for (let i = 0; i < 6; i++) {
        const a = (i * 60 - 90) * (Math.PI / 180);
        const outerX = cx + baseR * 1.02 * Math.cos(a);
        const outerY = cy + baseR * 1.02 * Math.sin(a);
        drawEdge(cx, cy, outerX, outerY, 0.12, '#8B9D83');
      }

      // Draw concentric circles
      [0.22, 0.44, 0.66, 0.88].forEach((r, i) => {
        ctx.beginPath();
        ctx.arc(cx, cy, baseR * r, 0, Math.PI * 2);
        ctx.strokeStyle = i % 2 === 0 ? '#8B9D83' : '#C97B63';
        ctx.globalAlpha = 0.18;
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      // Connect rings with edges
      const ring1 = nodes.filter(n => n[2] === 1);
      const ring2 = nodes.filter(n => n[2] === 2);
      const ring3 = nodes.filter(n => n[2] === 3);

      ring1.forEach((n, i) => {
        const next = ring1[(i + 1) % ring1.length];
        drawEdge(n[0], n[1], next[0], next[1], 0.25, '#8B9D83');
        const r2a = ring2[i * 2];
        const r2b = ring2[i * 2 + 1];
        if (r2a) drawEdge(n[0], n[1], r2a[0], r2a[1], 0.2, '#C97B63');
        if (r2b) drawEdge(n[0], n[1], r2b[0], r2b[1], 0.15, '#8B9D83');
      });

      ring2.forEach((n, i) => {
        const next = ring2[(i + 1) % ring2.length];
        drawEdge(n[0], n[1], next[0], next[1], 0.18, '#8B9D83');
        const r3a = ring3[Math.floor(i * 1.5) % ring3.length];
        if (r3a) drawEdge(n[0], n[1], r3a[0], r3a[1], 0.15, '#C97B63');
      });

      ring3.forEach((n, i) => {
        const next = ring3[(i + 1) % ring3.length];
        drawEdge(n[0], n[1], next[0], next[1], 0.15, '#8B9D83');
      });

      // Draw nodes
      nodes.forEach((n) => drawNode(n[0], n[1], n[2]));

      ctx.restore();
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-60"
    />
  );
}

const handleScroll = (href: string) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#1A1C20]">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/5313f837ceca8a723c7a70add5096fa4.jpg)',
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1C20]/70 via-[#1A1C20]/50 to-[#1A1C20]/90" />

      {/* Geodesic Canvas */}
      <GeodesicCanvas />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-[#8B9D83]/50 px-5 py-2 rounded-full mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8B9D83] inline-block" />
          <span className="text-[#8B9D83] text-xs uppercase tracking-[3px]">
            Plataforma de Re-ingeniería Sistémica
          </span>
        </div>

        {/* Headline */}
        <h1 className="mb-6">
          <span className="block text-[#F8F6F3] font-light leading-tight" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', letterSpacing: '-1px' }}>
            Nube Geodésica:
          </span>
          <span className="block font-light leading-tight" style={{ fontSize: 'clamp(2rem, 5vw, 4.2rem)', letterSpacing: '-1px' }}>
            <span className="text-[#8B9D83]">La Arquitectura</span>{' '}
            <span className="text-[#C97B63]">de tu Expansión.</span>
          </span>
        </h1>

        {/* Subline */}
        <p className="text-[#F8F6F3]/70 text-lg md:text-xl font-light max-w-xl mx-auto mb-10 leading-relaxed">
          Conecta los nodos dispersos de tu alma a través de una ingeniería sistémica sagrada.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => handleScroll('#iniciar-trazado')}
            className="bg-[#8B9D83] text-[#1A1C20] font-semibold text-base px-10 py-4 rounded-full hover:bg-[#7a8e72] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Traza tu Cartografía
          </button>
          <button
            onClick={() => handleScroll('#manifiesto')}
            className="border border-[#F8F6F3]/30 text-[#F8F6F3]/80 font-light text-base px-10 py-4 rounded-full hover:border-[#C97B63] hover:text-[#C97B63] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Explorar el Manifiesto
          </button>
        </div>

        {/* Live ticker */}
        <div className="mt-14 flex items-center justify-center gap-8 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-[#8B9D83] animate-pulse" />
            <span className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">
              1,200+ Nodos Activos
            </span>
          </div>
          <span className="text-[#F8F6F3]/20 text-xs">—</span>
          <span className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">
            6 Pilares Sistémicos
          </span>
          <span className="text-[#F8F6F3]/20 text-xs">—</span>
          <span className="text-[#F8F6F3]/50 text-xs uppercase tracking-[2px]">
            Alineación Nodal Activa
          </span>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-[#F8F6F3]/30 text-xs uppercase tracking-[2px]">Desplázate</span>
        <i className="ri-arrow-down-line text-[#F8F6F3]/30 text-lg" />
      </div>
    </section>
  );
}
